// api/panelDataFetcher.ts — per-symbol 1m REST seed for panelDataStore (design §4.4).
//
// loadSymbol1m fetches the 1m base candle series + trade flow for a symbol in
// parallel. Only 1m is ever fetched (the server caches only 1m, dataFanout.ts:159);
// higher-TF candle views are derived at render via aggregateCandles. Server-computed
// signal series (ema/rsi/…) arrive over WS, never REST.
//
// An AbortController is exposed so an in-flight load can be cancelled if the symbol
// is torn down before the REST round-trip lands (refcount 1→0 before resolve).

import { fetchCandles, fetchTradeFlow } from './restApi';
import {
  maxEnabledMaPeriod,
  requiredBaseCandles,
  SERVER_MAX_LIMIT,
} from './candleDepth';
import type { CandleData, CandleSource } from '../stores/priceDataStore';
import type { TradeFlowPoint } from '../stores/indicatorStore';

const FLOW_LIMIT = 1000;

export interface SymbolLoadResult {
  candles: CandleData[];
  flow: TradeFlowPoint[];
}

export interface SymbolLoadHandle {
  promise: Promise<SymbolLoadResult>;
  abort: () => void;
}

export class LoadAbortedError extends Error {
  constructor(symbol: string) {
    super(`panelDataFetcher: load aborted for ${symbol}`);
    this.name = 'LoadAbortedError';
  }
}

/**
 * Fetch the 1m base series + trade flow for a symbol. Returns a handle whose
 * `abort()` cancels the load (used when the symbol is dropped before the REST
 * round-trip lands — refcount 1→0 before resolve). The underlying `fetch*`
 * helpers don't accept an `AbortSignal`, so `abort()` cannot tear down the
 * socket; instead the resolved promise REJECTS with `LoadAbortedError` so the
 * store's `.then` never writes data for a symbol that no longer has a refcount.
 *
 * @param interval the requesting panel's timeframe in minutes (F1). The store
 *   stays 1m-base-only, but the DEPTH of the 1m fetch is scaled so the panel's
 *   aggregated TF view has ~7d (or MA-warmup) of bars — fixing the long-MA /
 *   warmup-indicator starvation that a fixed 720-bar fetch caused at 15m+.
 *   Defaults to 1 for non-chart (book/trades) subscribers that have no TF.
 * @param limitOverride when set, fetch exactly this many 1m bars instead of the
 *   depth-policy amount. Used by the two-phase first load: a small FAST seed for
 *   instant paint, followed by a background deep load (runEnsureLoaded).
 * @param onFlow optional callback invoked with the trade-flow series WHEN IT ARRIVES,
 *   independently of (and possibly long after) the candle promise resolves. The
 *   /api/trade-flow query is slow (6s+ for BTC) and MUST NOT block the candle paint;
 *   the caller seeds candles on resolve and merges flow later via this callback.
 */
export function loadSymbol1m(
  symbol: string,
  interval = 1,
  limitOverride?: number,
  onFlow?: (flow: TradeFlowPoint[]) => void,
): SymbolLoadHandle {
  const controller = new AbortController();
  // DEPTH policy (F1): 1m bars needed so aggregateCandles yields ~7d of the
  // panel's TF after derivation. Read maxMa via getState() — this is a fetcher
  // path, not a component, so a hook subscription would be wrong here.
  const candleLimit = limitOverride ?? requiredBaseCandles(interval, maxEnabledMaPeriod());

  // Trade flow is a SECONDARY overlay and its query is slow/flaky for high-volume symbols
  // (BTC /api/trade-flow can take 6s+). Fire it as a TRULY independent request that never
  // blocks or fails the candle paint — when it lands, push it to the store via onFlow. We
  // previously `await`ed it before resolving, which held the candles hostage to the 6s flow
  // query (the reported "first load slow" bug). Now candles resolve the instant they arrive.
  if (onFlow) {
    void fetchTradeFlow(symbol, 1, FLOW_LIMIT)
      .then((flowRes) => {
        if (!controller.signal.aborted) onFlow(flowRes.tradeFlow.map(toTradeFlowPoint));
      })
      .catch(() => {
        // Slow/504/unmaterialized — the chart already rendered without it; no-op.
      });
  }

  const promise = (async (): Promise<SymbolLoadResult> => {
    const candleRes = await fetchCandles(symbol, 1, candleLimit);
    if (controller.signal.aborted) throw new LoadAbortedError(symbol);
    // flow always [] here — it arrives asynchronously via onFlow, never blocking candles.
    return {
      candles: candleRes.candles.map(toCandleData),
      flow: [],
    };
  })();
  return { promise, abort: () => controller.abort() };
}

/**
 * Fetch a window of OLDER 1m candles for left-edge infinite-scroll (design §4, BUILD #1).
 *
 * Bounds the request to `[from, to)` where `to = beforeTs` (the current oldest candle's
 * startTs, EXCLUSIVE — the server returns rows strictly older so dedupeSorted in the store
 * never has to discard overlap) and `from = to - spanMs`. Only the 1m base is fetched
 * (higher-TF views derive via aggregateCandles); trade flow is NOT pulled — older history is
 * a price-only backfill and the flow overlay only spans the live window.
 *
 * `spanMs` is a TIME span, not a row count, because the caller's target is a number of bars
 * at the PANEL's timeframe and the store only stores 1m. A 1d screen spans months of 1m rows,
 * far more than the server's 10000-row cap — see olderSpanMsFor.
 *
 * Returns the same `{ promise, abort }` handle shape as `loadSymbol1m`. `abort()` flips the
 * resolved promise to REJECT with `LoadAbortedError` (the underlying fetch can't be cancelled),
 * so a torn-down symbol never prepends stale candles.
 */
export function loadOlderCandles(
  symbol: string,
  beforeTs: number,
  spanMs = 200 * 60_000,
): SymbolLoadHandle {
  const controller = new AbortController();
  const promise = (async (): Promise<SymbolLoadResult> => {
    const to = beforeTs;
    // /api/candles orders ASC then LIMITs, so a window holding more than SERVER_MAX_LIMIT 1m
    // rows returns the rows nearest `from` and STRANDS a hole before `to`. Prepending that
    // leaves the series discontinuous (fillCandleGaps would paper over it with synthetic
    // bars). Clamp the window to what one request can return contiguously; successive
    // scrolls then walk `to` further back, each extending history without a hole.
    const from = Math.max(to - spanMs, to - SERVER_MAX_LIMIT * 60_000);
    const candleRes = await fetchCandles(symbol, 1, SERVER_MAX_LIMIT, from, to);
    if (controller.signal.aborted) throw new LoadAbortedError(symbol);
    // Drop any row at/after the boundary so the prepend stays strictly older than the
    // current oldest (server `to` is sometimes inclusive); dedupeSorted would merge a
    // boundary dup, but filtering keeps the "did we gain new rows?" count honest.
    const older = candleRes.candles
      .map(toCandleData)
      .filter((c) => c.startTs < beforeTs);
    return { candles: older, flow: [] };
  })();
  return { promise, abort: () => controller.abort() };
}

function toCandleData(c: {
  startTs: number;
  open: number;
  high: number;
  low: number;
  close: number;
  volume: number;
  source?: CandleSource;
}): CandleData {
  // exactOptionalPropertyTypes: only set `source` when present.
  return c.source != null
    ? {
        startTs: c.startTs,
        open: c.open,
        high: c.high,
        low: c.low,
        close: c.close,
        volume: c.volume,
        source: c.source,
      }
    : {
        startTs: c.startTs,
        open: c.open,
        high: c.high,
        low: c.low,
        close: c.close,
        volume: c.volume,
      };
}

function toTradeFlowPoint(p: {
  startTs: number;
  signedVolume: number | null;
  buyVolume: number | null;
  sellVolume: number | null;
  tradeImbalance: number | null;
  largeTradeCount: number | null;
  tradeCount: number | null;
}): TradeFlowPoint {
  return {
    startTs: p.startTs,
    signedVolume: p.signedVolume,
    buyVolume: p.buyVolume,
    sellVolume: p.sellVolume,
    tradeImbalance: p.tradeImbalance,
    largeTradeCount: p.largeTradeCount,
    tradeCount: p.tradeCount,
  };
}
